import React from 'react';
import { useTranslate } from '@refinedev/core';
import { FaInfoCircle, FaExclamationCircle } from 'react-icons/fa';
import { startCase, toLower, uniqBy } from 'lodash';
import type { FieldSchema } from '~/resources/loyalty/stamp-template/hooks/useStampingSchemas';
import { formatCentsToRinggit, formatRinggitToCents } from '~/utils/currency';
import { CurrencyInput } from '~/components/CurrencyInput';

interface DynamicFieldGeneratorProps {
  schema: Record<string, FieldSchema>;
  values: Record<string, any>;
  onChange: (values: Record<string, any>) => void;
  errors?: Record<string, string>;
  className?: string;
  disabled?: boolean;
}

export const DynamicFieldGenerator: React.FC<DynamicFieldGeneratorProps> = ({
  schema,
  values = {},
  onChange,
  errors = {},
  className = '',
  disabled = false
}) => {
  const t = useTranslate();
  // Handle individual field changes
  const handleFieldChange = (key: string, value: any) => {
    const newValues = { ...values, [key]: value };
    
    // Apply defaults for empty required fields
    Object.entries(schema).forEach(([fieldKey, fieldSchema]) => {
      if (fieldSchema.required && !newValues[fieldKey] && fieldSchema.default !== undefined) {
        newValues[fieldKey] = fieldSchema.default;
      }
    });
    
    onChange(newValues);
  };

  // Render individual field based on type
  const renderField = (key: string, fieldSchema: FieldSchema) => {
    const value = values[key] ?? fieldSchema.default ?? '';
    const error = errors[key];
    const fieldId = `field-${key}-${Math.random().toString(36).substr(2, 9)}`;

    switch (fieldSchema.type) {
      case 'select':
        return (
          <div key={key} className="space-y-2">
            <label htmlFor={fieldId} className="flex items-center gap-2 text-sm font-medium text-gray-700">
              {formatLabel(key)}
              {fieldSchema.required && <span className="text-red-500">*</span>}
            </label>
            <select
              id={fieldId}
              value={value}
              onChange={(e) => handleFieldChange(key, e.target.value)}
              disabled={disabled}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors ${
                error ? 'border-red-500' : 'border-gray-300'
              } ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
            >
              <option value="">{t('stampCard.dynamicField.selectPlaceholder', { field: formatLabel(key) })}</option>
              {(() => {
                // Deduplicate options based on normalized form
                // This converts variations like "Sub Total", "subtotal", "SubTotal" to the same normalized form
                const uniqueOptions = uniqBy(fieldSchema.options || [], (option) => {
                  // Normalize by converting to lowercase and then to start case for comparison
                  // This handles: "orderAmount" -> "Order Amount", "Sub Total" -> "Sub Total", etc.
                  return startCase(toLower(option));
                });
                
                return uniqueOptions;
              })().map((option) => (
                <option key={option} value={option}>
                  {formatOptionLabel(option)}
                </option>
              ))}
            </select>
            {renderHelperText(fieldSchema.helperText, error)}
          </div>
        );

      case 'number':
        // Handle currency display type
        if (fieldSchema.displayType === 'currency') {
          return (
            <div key={key} className="space-y-2">
              <label htmlFor={fieldId} className="flex items-center gap-2 text-sm font-medium text-gray-700">
                {formatLabel(key)}
                {fieldSchema.required && <span className="text-red-500">*</span>}
              </label>
              <CurrencyInput
                value={value || 0}
                onChange={(newValue) => handleFieldChange(key, newValue)}
                placeholder={fieldSchema.placeholder || '0.00'}
                disabled={disabled}
                error={error}
                helperText={fieldSchema.helperText}
                min={fieldSchema.min}
                max={fieldSchema.max}
              />
            </div>
          );
        }

        // Default number input
        return (
          <div key={key} className="space-y-2">
            <label htmlFor={fieldId} className="flex items-center gap-2 text-sm font-medium text-gray-700">
              {formatLabel(key)}
              {fieldSchema.required && <span className="text-red-500">*</span>}
            </label>
            <input
              id={fieldId}
              type="number"
              value={value}
              onChange={(e) => {
                const val = e.target.value === '' ? '' : parseFloat(e.target.value);
                handleFieldChange(key, val);
              }}
              min={fieldSchema.min}
              max={fieldSchema.max}
              placeholder={fieldSchema.placeholder}
              disabled={disabled}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors ${
                error ? 'border-red-500' : 'border-gray-300'
              } ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
            />
            {renderHelperText(fieldSchema.helperText, error)}
          </div>
        );

      case 'boolean':
        return (
          <div key={key} className="space-y-2">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <label htmlFor={fieldId} className="text-sm font-medium text-gray-700 cursor-pointer">
                {formatLabel(key)}
                {fieldSchema.required && <span className="text-red-500 ml-1">*</span>}
              </label>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  id={fieldId}
                  type="checkbox"
                  checked={value === true}
                  onChange={(e) => handleFieldChange(key, e.target.checked)}
                  disabled={disabled}
                  className="sr-only peer"
                />
                <div className={`w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600 ${
                  disabled ? 'opacity-50 cursor-not-allowed' : ''
                }`}></div>
              </label>
            </div>
            {renderHelperText(fieldSchema.helperText, error)}
          </div>
        );

      case 'string':
      default:
        return (
          <div key={key} className="space-y-2">
            <label htmlFor={fieldId} className="flex items-center gap-2 text-sm font-medium text-gray-700">
              {formatLabel(key)}
              {fieldSchema.required && <span className="text-red-500">*</span>}
            </label>
            <input
              id={fieldId}
              type="text"
              value={value}
              onChange={(e) => handleFieldChange(key, e.target.value)}
              placeholder={fieldSchema.placeholder}
              disabled={disabled}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors ${
                error ? 'border-red-500' : 'border-gray-300'
              } ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
            />
            {renderHelperText(fieldSchema.helperText, error)}
          </div>
        );
    }
  };

  // Helper function to render helper text or error
  const renderHelperText = (helperText?: string, error?: string) => {
    if (error) {
      return (
        <p className="text-xs text-red-600 flex items-center gap-1 mt-1">
          <FaExclamationCircle className="flex-shrink-0" />
          <span>{error}</span>
        </p>
      );
    }
    if (helperText) {
      return (
        <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
          <FaInfoCircle className="text-gray-400 flex-shrink-0" />
          <span>{helperText}</span>
        </p>
      );
    }
    return null;
  };

  // Format field key to readable label
  const formatLabel = (key: string): string => {
    // Handle common abbreviations
    const specialCases: Record<string, string> = {
      'minReferrals': 'Minimum Referrals',
      'maxStamps': 'Maximum Stamps',
      'stampsPerReferral': 'Stamps Per Referral',
    };
    
    if (specialCases[key]) {
      return specialCases[key];
    }
    
    return key
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (str) => str.toUpperCase())
      .trim();
  };

  // Format option value to readable label
  const formatOptionLabel = (value: string): string => {
    const amountFields: Record<string, string> = {
      'totalAmount': t('stampCard.amountFields.totalAmount'),
      'subtotal': t('stampCard.amountFields.subtotal'),
    };

    const operators: Record<string, string> = {
      '>': t('stampCard.operators.greaterThan'),
      '>=': t('stampCard.operators.greaterThanEqual'),
      '==': t('stampCard.operators.equalTo'),
      '!=': t('stampCard.operators.notEqualTo'),
      '<': t('stampCard.operators.lessThan'),
      '<=': t('stampCard.operators.lessThanEqual'),
    };

    const tiers: Record<string, string> = {
      'bronze': t('stampCard.tiers.bronze'),
      'silver': t('stampCard.tiers.silver'),
      'gold': t('stampCard.tiers.gold'),
      'platinum': t('stampCard.tiers.platinum'),
    };
    
    // Use lodash startCase to handle all case formats elegantly
    // Converts: "has_placed_order" -> "Has Placed Order"
    //           "hasPlacedOrder" -> "Has Placed Order"  
    //           "has-placed-order" -> "Has Placed Order"
    return amountFields[value] || operators[value] || tiers[value] || startCase(value);
  };

  // Handle empty schema
  if (!schema || Object.keys(schema).length === 0) {
    return (
      <div className="text-sm text-gray-500 italic p-4 bg-gray-50 rounded-lg border border-gray-200">
        {t('stampCard.dynamicField.noConfiguration')}
      </div>
    );
  }

  return (
    <div className={`space-y-4 ${className}`}>
      {Object.entries(schema).map(([key, fieldSchema]) => renderField(key, fieldSchema))}
    </div>
  );
};

export default DynamicFieldGenerator;